import { ProfileResponse } from "@/types";
import { ParsedResponse, SnakeToCamelCase } from "@/types/utils/CaseTypes";

type SpecialKeys<T> = {
  [key in keyof Partial<T>]: string;
};

/**
 * A special keys mapper used for being compatible with the current way the vignette frontend parses
 * the profile response.
 */
export const memberSpecialKeys: SpecialKeys<ProfileResponse> = {
  briefcase_item_id: "briefcaseItemId",
};
/**
 * Parse the key from snake_case to camelCase.
 * @param value
 */
const parseKey = (value: string): SnakeToCamelCase<string> => {
  return value
    .replaceAll("__", "_")
    .replace(/(_\w)/g, (matched) => matched[1]?.toUpperCase() ?? "");
};

/**
 * Parse the response object to camelCase. This works recursively to parse nested objects and arrays.
 *
 * Future improvements to this might include automatically handling briefcase status
 * @param response
 * @returns {ParsedResponse<T>} The parsed response object with all keys converted to camelCase.
 *
 * @example When working with lists
 * const parsedList = list.map(parseResponse);
 * @example When working with nullable objects
 * const parsedObject = response.nullable ? parseResponse(object) : null;
 */
export const parseResponse = <T>(response: T): ParsedResponse<T> => {
  if (typeof response !== "object" || response === null) {
    throw new Error("Response must be a non-null object");
  }
  const responseRecord = response as Record<string, any>;
  const result: any = {};
  for (const key of Object.keys(responseRecord)) {
    let newKey;
    if (memberSpecialKeys && memberSpecialKeys[key]) {
      newKey = memberSpecialKeys[key];
    } else {
      newKey = parseKey(key);
    }
    if (newKey === undefined) throw new Error("Undefined key");
    const value = responseRecord[key];
    if (value && typeof value === "object") {
      if (Array.isArray(value)) {
        if (typeof value[0] === "object") {
          result[newKey] = value.map((v) => parseResponse(v));
        } else if (value[0]) {
          result[newKey] = value;
        } else if (value.length === 0) {
          result[newKey] = [];
        }
      } else {
        result[newKey] = parseResponse(value);
      }
    } else {
      result[newKey] = value;
    }
  }
  return result as ParsedResponse<T>;
};
